// frontend/src/pages/KanbanInvitaciones.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import kanbanService from '../services/kanbanService';
import { toast } from 'react-toastify';
import Loading from '../components/Loading';
import './KanbanInvitaciones.css';

const KanbanInvitaciones = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token: tokenFromUrl } = useParams();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingToken, setProcessingToken] = useState(null);

  useEffect(() => {
    cargarInvitaciones();

    // Si hay un token en la URL (como parámetro de ruta o query string), procesarlo automáticamente
    const token = tokenFromUrl || searchParams.get('token');
    if (token) {
      handleAcceptFromUrl(token);
    }
  }, [searchParams, tokenFromUrl]);

  const cargarInvitaciones = async () => {
    try {
      setLoading(true);
      const data = await kanbanService.getMyInvitations();
      setInvitations(data.invitations || []);
    } catch (error) {
      console.error('Error al cargar invitaciones:', error);
      toast.error('Error al cargar invitaciones');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptFromUrl = async (token) => {
    setProcessingToken(token);
    try {
      const response = await kanbanService.acceptInvitation(token);
      toast.success(`¡Invitación aceptada! Te has unido a "${response.board_nombre}"`);

      // Limpiar el token de la URL
      navigate('/kanban/invitaciones', { replace: true });

      // Recargar invitaciones
      cargarInvitaciones();

      // Redirigir al tablero después de 2 segundos
      setTimeout(() => {
        navigate(`/kanban/tablero/${response.board_id}`);
      }, 2000);
    } catch (error) {
      console.error('Error al aceptar invitación:', error);
      const errorMsg = error.response?.data?.detail || 'Error al aceptar la invitación';
      toast.error(errorMsg);

      // Limpiar el token de la URL incluso si hay error
      navigate('/kanban/invitaciones', { replace: true });
    } finally {
      setProcessingToken(null);
    }
  };

  const handleAccept = async (token, boardName) => {
    if (window.confirm(`¿Aceptar invitación al tablero "${boardName}"?`)) {
      setProcessingToken(token);
      try {
        const response = await kanbanService.acceptInvitation(token);
        toast.success(`¡Te has unido a "${boardName}"!`);
        cargarInvitaciones();

        // Redirigir al tablero
        setTimeout(() => {
          navigate(`/kanban/tablero/${response.board_id}`);
        }, 1500);
      } catch (error) {
        console.error('Error al aceptar invitación:', error);
        const errorMsg = error.response?.data?.detail || 'Error al aceptar la invitación';
        toast.error(errorMsg);
      } finally {
        setProcessingToken(null);
      }
    }
  };

  const handleDecline = async (token, boardName) => {
    if (window.confirm(`¿Rechazar invitación al tablero "${boardName}"?`)) {
      setProcessingToken(token);
      try {
        await kanbanService.declineInvitation(token);
        toast.success('Invitación rechazada');
        cargarInvitaciones();
      } catch (error) {
        console.error('Error al rechazar invitación:', error);
        toast.error('Error al rechazar la invitación');
      } finally {
        setProcessingToken(null);
      }
    }
  };

  const getRolLabel = (rol) => {
    const roles = {
      'owner': 'Propietario',
      'editor': 'Editor',
      'viewer': 'Visualizador'
    };
    return roles[rol] || rol;
  };

  const getRolIcon = (rol) => {
    const icons = {
      'owner': 'fa-crown',
      'editor': 'fa-pencil-alt',
      'viewer': 'fa-eye'
    };
    return icons[rol] || 'fa-user';
  };

  const getRolColor = (rol) => {
    const colors = {
      'owner': '#f59e0b',
      'editor': '#3b82f6',
      'viewer': '#6b7280'
    };
    return colors[rol] || '#6b7280';
  };

  const isExpiringSoon = (expiresAt) => {
    const expireDate = new Date(expiresAt);
    const now = new Date();
    const hoursDiff = (expireDate - now) / (1000 * 60 * 60);
    return hoursDiff < 24 && hoursDiff > 0;
  };

  const isExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date();
  };

  const getTimeRemaining = (expiresAt) => {
    const expireDate = new Date(expiresAt);
    const now = new Date();
    const diff = expireDate - now;

    if (diff < 0) return 'Expirada';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return `${days} día${days > 1 ? 's' : ''}`;
    if (hours > 0) return `${hours} hora${hours > 1 ? 's' : ''}`;
    return `${minutes} minuto${minutes > 1 ? 's' : ''}`;
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="kanban-invitaciones-container">
      <div className="invitaciones-header">
        <div>
          <h1>
            <i className="fas fa-envelope"></i> Mis Invitaciones
          </h1>
          <p className="text-muted">
            Invitaciones pendientes a tableros Kanban
          </p>
        </div>
        <button
          className="estado-btn"
          onClick={() => navigate('/kanban')}
        >
          <i className="fas fa-arrow-left"></i> Volver a tableros
        </button>
      </div>

      {processingToken && (
        <div className="processing-banner">
          <Loading inline size="sm" mensaje="Procesando invitación" />

        </div>
      )}

      <div className="invitaciones-content">
        {invitations.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-inbox fa-3x"></i>
            <h3>No tienes invitaciones pendientes</h3>
            <p>Cuando alguien te invite a un tablero, aparecerá aquí</p>
            <button
              className="btn-primary"
              onClick={() => navigate('/kanban')}
            >
              <i className="fas fa-th-large"></i> Ver mis tableros
            </button>
          </div>
        ) : (
          <div className="invitaciones-grid">
            {invitations.map(invitation => {
              const expired = isExpired(invitation.expires_at);
              const expiringSoon = isExpiringSoon(invitation.expires_at);

              return (
                <div
                  key={invitation.id}
                  className={`invitation-card ${expired ? 'expired' : ''} ${expiringSoon ? 'expiring-soon' : ''}`}
                >
                  <div className="invitation-card-header">
                    <div className="board-info">
                      <span className="board-icon">{invitation.tablero_icono}</span>
                      <div>
                        <h3>{invitation.tablero_nombre}</h3>
                        <p className="board-owner">
                          <i className="fas fa-user"></i>
                          Invitado por: {invitation.invited_by_nombre}
                        </p>
                      </div>
                    </div>

                    <div
                      className="role-badge"
                      style={{ backgroundColor: getRolColor(invitation.rol) }}
                    >
                      <i className={`fas ${getRolIcon(invitation.rol)}`}></i>
                      {getRolLabel(invitation.rol)}
                    </div>
                  </div>

                  {invitation.tablero_descripcion && (
                    <p className="board-description">
                      {invitation.tablero_descripcion}
                    </p>
                  )}

                  <div className="invitation-details">
                    <div className="detail-item">
                      <i className="fas fa-calendar-plus"></i>
                      <span>
                        Recibida: {new Date(invitation.invited_at).toLocaleDateString()}
                      </span>
                    </div>

                    <div className={`detail-item ${expired ? 'text-danger' : expiringSoon ? 'text-warning' : ''}`}>
                      <i className="fas fa-clock"></i>
                      <span>
                        {expired ? (
                          'Expirada'
                        ) : (
                          <>
                            Expira en: {getTimeRemaining(invitation.expires_at)}
                          </>
                        )}
                      </span>
                    </div>
                  </div>

                  {expired && (
                    <div className="expired-banner">
                      <i className="fas fa-exclamation-circle"></i>
                      Esta invitación ha expirado
                    </div>
                  )}

                  {expiringSoon && !expired && (
                    <div className="warning-banner">
                      <i className="fas fa-exclamation-triangle"></i>
                      ¡Esta invitación expira pronto!
                    </div>
                  )}

                  <div className="invitation-actions">
                    {!expired ? (
                      <>
                        <button
                          className="btn-accept"
                          onClick={() => handleAccept(invitation.token, invitation.tablero_nombre)}
                          disabled={processingToken === invitation.token}
                        >
                          <i className="fas fa-check"></i>
                          {processingToken === invitation.token ? 'Procesando...' : 'Aceptar'}
                        </button>
                        <button
                          className="btn-decline"
                          onClick={() => handleDecline(invitation.token, invitation.tablero_nombre)}
                          disabled={processingToken === invitation.token}
                        >
                          <i className="fas fa-times"></i>
                          Rechazar
                        </button>
                      </>
                    ) : (
                      <button
                        className="btn-decline"
                        onClick={() => handleDecline(invitation.token, invitation.tablero_nombre)}
                        disabled={processingToken === invitation.token}
                      >
                        <i className="fas fa-trash"></i>
                        Eliminar
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="invitaciones-help">
        <div className="help-card">
          <h4>
            <i className="fas fa-question-circle"></i> Acerca de las invitaciones
          </h4>
          <ul>
            <li>
              <strong>Visualizador:</strong> Solo puedes ver el tablero y sus tareas, sin realizar cambios.
            </li>
            <li>
              <strong>Editor:</strong> Puedes crear, editar y eliminar tareas, así como usar el cronómetro.
            </li>
            <li>
              <strong>Propietario:</strong> Control total del tablero, incluyendo gestión de miembros y configuración.
            </li>
            <li>
              Las invitaciones expiran automáticamente después de 7 días.
            </li>
            <li>
              Puedes aceptar invitaciones desde el enlace enviado a tu email o desde esta página.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default KanbanInvitaciones;